/**
 * System Characteristic Detail Page
 * Admin interface for viewing and editing a single system characteristic
 *
 * Important: System characteristics are global, admin-owned, and high-impact
 * Changes affect all agents using them
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { ArrowLeft, Save, AlertTriangle, X } from 'lucide-react';
import {
  useSystemCharacteristic,
  useUpdateSystemCharacteristic,
} from '../../services/systemCharacteristic.service';
import { FormattedNumberTextField } from '../../components/common/FormattedNumberTextField';

const CHARACTERISTIC_LAYERS = [
  { value: 'identity', label: 'Identity' },
  { value: 'tone_style', label: 'Tone & Style' },
  { value: 'values', label: 'Values & Priorities' },
  { value: 'behavior', label: 'Behavioral Rules' },
  { value: 'constraints', label: 'Constraints' },
  { value: 'domain', label: 'Domain Guidance' },
];

const CHARACTERISTIC_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const CHARACTERISTIC_VISIBILITIES = [
  {
    value: 'private',
    label: 'Private',
    helper: 'Only available to internal system/admin workflows',
  },
  {
    value: 'public',
    label: 'Public',
    helper: 'Available for users to attach to their agents',
  },
];

const getCharacteristicOwnerLabel = (characteristic: {
  isSystem: boolean;
  ownerUser?: {
    email: string;
    userName: string | null;
    fullName: string | null;
  } | null;
}) => {
  if (characteristic.isSystem) return 'System';

  const owner = characteristic.ownerUser;
  if (!owner) return 'Unknown user';

  return owner.fullName || owner.userName || owner.email;
};

// ============================================
// System Characteristic Detail Component
// ============================================

export const SystemCharacteristicDetail: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);

  const { data: characteristic, isLoading, error } = useSystemCharacteristic(publicId || '');
  const updateMutation = useUpdateSystemCharacteristic();

  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    layer: 'behavior',
    sortOrder: 100,
    priority: 100,
    status: 'published',
    visibility: 'private',
    isDefault: false,
    prompt: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Reset form when characteristic changes
  React.useEffect(() => {
    if (characteristic) {
      setFormValues({
        name: characteristic.name,
        description: characteristic.description || '',
        layer: characteristic.layer || 'behavior',
        sortOrder: characteristic.sortOrder ?? 100,
        priority: characteristic.priority ?? 100,
        status: characteristic.status || 'published',
        visibility: characteristic.visibility === 'private' ? 'private' : 'public',
        isDefault: characteristic.isDefault ?? false,
        prompt: characteristic.prompt,
      });
      setFormErrors({});
    }
  }, [characteristic]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSelectChange =
    (field: 'layer' | 'status' | 'visibility') => (event: SelectChangeEvent<string>) => {
      handleChange(field, event.target.value);
    };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formValues.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formValues.prompt.trim()) {
      newErrors.prompt = 'Prompt is required';
    }

    if (formValues.sortOrder < 0 || formValues.sortOrder > 10000) {
      newErrors.sortOrder = 'Sort order must be between 0 and 10000';
    }

    if (formValues.priority < 0 || formValues.priority > 10000) {
      newErrors.priority = 'Priority must be between 0 and 10000';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !characteristic) return;

    try {
      await updateMutation.mutateAsync({
        publicId: characteristic.publicId,
        name: formValues.name.trim(),
        description: formValues.description.trim() || null,
        layer: formValues.layer,
        sortOrder: formValues.sortOrder,
        priority: formValues.priority,
        status: formValues.status,
        visibility: formValues.visibility as 'public' | 'private',
        isDefault: formValues.isDefault,
        prompt: formValues.prompt.trim(),
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating system characteristic:', error);
    }
  };

  const handleCancel = () => {
    if (characteristic) {
      setFormValues({
        name: characteristic.name,
        description: characteristic.description || '',
        layer: characteristic.layer || 'behavior',
        sortOrder: characteristic.sortOrder ?? 100,
        priority: characteristic.priority ?? 100,
        status: characteristic.status || 'published',
        visibility: characteristic.visibility === 'private' ? 'private' : 'public',
        isDefault: characteristic.isDefault ?? false,
        prompt: characteristic.prompt,
      });
      setFormErrors({});
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <Box className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !characteristic) {
    return (
      <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
        <Paper className="rounded-xl p-6">
          <Typography className="text-red-600 dark:text-red-400">
            Failed to load system characteristic. Please try again.
          </Typography>
          <Button
            onClick={() => navigate('/system-characteristics')}
            startIcon={<ArrowLeft size={16} />}
            className="mt-4"
          >
            Back to List
          </Button>
        </Paper>
      </Box>
    );
  }

  const isReadOnly = characteristic.isEditable === false;
  const normalizedVisibility = characteristic.visibility === 'private' ? 'Private' : 'Public';
  const ownerLabel = getCharacteristicOwnerLabel(characteristic);

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      {/* Header */}
      <Box className="mb-6 flex items-center justify-between">
        <Box>
          <Button
            onClick={() => navigate('/system-characteristics')}
            startIcon={<ArrowLeft size={16} />}
            className="mb-2 text-gray-600 dark:text-slate-400"
          >
            Back to List
          </Button>
          <Typography variant="h4" className="font-bold text-gray-900 dark:text-slate-100">
            {characteristic.name}
          </Typography>
          <Typography variant="body2" className="mt-1 text-gray-600 dark:text-slate-400">
            Characteristic Detail
          </Typography>
        </Box>

        <Box className="flex gap-2">
          {!isEditing && !isReadOnly && (
            <Button
              variant="outlined"
              onClick={() => setIsEditing(true)}
              className="border-blue-600 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400"
            >
              Edit
            </Button>
          )}
          {isEditing && (
            <>
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<X size={16} />}
                disabled={updateMutation.isPending}
                className="text-gray-600 dark:text-slate-400"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={
                  updateMutation.isPending ? <CircularProgress size={16} /> : <Save size={16} />
                }
                disabled={updateMutation.isPending}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Warning Banner - Always visible */}
      {isReadOnly ? (
        <Alert severity="info" className="mb-4">
          <Typography variant="body2" className="font-medium">
            {characteristic.isSystem
              ? 'This default characteristic is hardcoded in the backend and cannot be edited from admin.'
              : 'This characteristic belongs to a user and is read-only in this admin screen.'}
          </Typography>
        </Alert>
      ) : isEditing ? (
        <Alert severity="warning" className="mb-4" icon={<AlertTriangle size={20} />}>
          <Typography variant="body2" className="font-medium">
            Private characteristics are for internal system use. Public characteristics can be
            attached by users to their agents.
          </Typography>
        </Alert>
      ) : null}

      {/* Main Content */}
      <Paper className="rounded-xl p-6 shadow-sm">
        <Box className="space-y-6">
          {/* Metadata Section */}
          <Box>
            <Typography
              variant="h6"
              className="mb-4 font-semibold text-gray-900 dark:text-slate-100"
            >
              Metadata
            </Typography>

            <Box className="space-y-4">
              {/* Public ID - Read-only */}
              <TextField
                fullWidth
                label="Public ID"
                value={characteristic.publicId}
                helperText="Unique identifier (read-only)"
                disabled
                InputProps={{
                  style: { fontFamily: 'monospace' },
                }}
              />

              {/* Code - Read-only (immutable) */}
              <TextField
                fullWidth
                label="Code"
                value={characteristic.code}
                helperText="Unique code identifier (immutable after creation)"
                disabled
                InputProps={{
                  style: { fontFamily: 'monospace' },
                }}
              />

              <Box className="flex flex-wrap items-center gap-2">
                <Typography variant="body2" className="text-gray-700 dark:text-slate-300">
                  Source:
                </Typography>
                <Chip
                  label={characteristic.isSystem ? 'System' : 'User'}
                  size="small"
                  className={
                    characteristic.isSystem
                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                      : 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
                  }
                />
                <Chip
                  label={characteristic.source === 'default' ? 'Default source' : 'Database'}
                  size="small"
                  className={
                    characteristic.source === 'default'
                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
                  }
                />
                <Chip
                  label={normalizedVisibility}
                  size="small"
                  className={
                    normalizedVisibility === 'Public'
                      ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                  }
                />
                <Chip
                  label={characteristic.isDefault ? 'Default fallback' : 'Not default'}
                  size="small"
                  className={
                    characteristic.isDefault
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
                  }
                />
              </Box>

              <TextField
                fullWidth
                label="Owner"
                value={
                  characteristic.isSystem
                    ? ownerLabel
                    : `${ownerLabel}${characteristic.ownerUser?.email ? ` (${characteristic.ownerUser.email})` : ''}`
                }
                helperText={
                  characteristic.isSystem
                    ? 'Global admin-owned characteristic'
                    : 'User-owned characteristic shown for admin inspection'
                }
                disabled
              />

              {/* Name */}
              <TextField
                fullWidth
                label="Name"
                value={formValues.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name || 'Display name for the system characteristic'}
                disabled={!isEditing || isReadOnly || updateMutation.isPending}
                required
              />

              <TextField
                fullWidth
                label="Description"
                value={formValues.description}
                onChange={(e) => handleChange('description', e.target.value)}
                helperText="Short admin note for what this characteristic controls"
                disabled={!isEditing || isReadOnly || updateMutation.isPending}
              />

              <Box className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormControl
                  fullWidth
                  error={!!formErrors.layer}
                  disabled={!isEditing || isReadOnly || updateMutation.isPending}
                  required
                >
                  <InputLabel>Layer</InputLabel>
                  <Select
                    label="Layer"
                    value={formValues.layer}
                    onChange={handleSelectChange('layer')}
                  >
                    {CHARACTERISTIC_LAYERS.map((layer) => (
                      <MenuItem key={layer.value} value={layer.value}>
                        {layer.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formErrors.layer || 'Prompt section used during compilation'}
                  </FormHelperText>
                </FormControl>

                <FormControl
                  fullWidth
                  error={!!formErrors.status}
                  disabled={!isEditing || isReadOnly || updateMutation.isPending}
                  required
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={formValues.status}
                    onChange={handleSelectChange('status')}
                  >
                    {CHARACTERISTIC_STATUSES.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formErrors.status || 'Only published public characteristics are user-usable'}
                  </FormHelperText>
                </FormControl>

                <FormControl
                  fullWidth
                  error={!!formErrors.visibility}
                  disabled={!isEditing || isReadOnly || updateMutation.isPending}
                  required
                >
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    label="Visibility"
                    value={formValues.visibility}
                    onChange={handleSelectChange('visibility')}
                  >
                    {CHARACTERISTIC_VISIBILITIES.map((visibility) => (
                      <MenuItem key={visibility.value} value={visibility.value}>
                        {visibility.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {formErrors.visibility ||
                      CHARACTERISTIC_VISIBILITIES.find(
                        (item) => item.value === formValues.visibility
                      )?.helper}
                  </FormHelperText>
                </FormControl>
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formValues.isDefault}
                      onChange={(event) => handleChange('isDefault', event.target.checked)}
                      disabled={!isEditing || isReadOnly || updateMutation.isPending}
                    />
                  }
                  label="Default fallback for this layer"
                />
                <Typography variant="caption" className="block text-gray-500 dark:text-slate-500">
                  Used automatically when an agent has no configured characteristics.
                </Typography>
              </Box>

              <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormattedNumberTextField
                  fullWidth
                  label="Sort Order"
                  value={formValues.sortOrder}
                  onValueChange={(value) => handleChange('sortOrder', value ?? 0)}
                  error={!!formErrors.sortOrder}
                  helperText={formErrors.sortOrder || 'Lower values appear earlier in the layer'}
                  disabled={!isEditing || isReadOnly || updateMutation.isPending}
                  inputProps={{ min: 0, max: 10000 }}
                />

                <FormattedNumberTextField
                  fullWidth
                  label="Priority"
                  value={formValues.priority}
                  onValueChange={(value) => handleChange('priority', value ?? 0)}
                  error={!!formErrors.priority}
                  helperText={formErrors.priority || 'Reserved for conflict resolution'}
                  disabled={!isEditing || isReadOnly || updateMutation.isPending}
                  inputProps={{ min: 0, max: 10000 }}
                />
              </Box>

              {/* Usage Count */}
              <Box className="flex items-center gap-2">
                <Typography variant="body2" className="text-gray-700 dark:text-slate-300">
                  Usage Count:
                </Typography>
                <Chip
                  label={characteristic.usageCount || 0}
                  size="small"
                  className={
                    (characteristic.usageCount || 0) > 0
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
                  }
                />
                <Typography variant="caption" className="text-gray-500 dark:text-slate-500">
                  ({characteristic.usageCount || 0} agent
                  {(characteristic.usageCount || 0) !== 1 ? 's' : ''} using this characteristic)
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Prompt Section */}
          <Box>
            <Typography
              variant="h6"
              className="mb-4 font-semibold text-gray-900 dark:text-slate-100"
            >
              Prompt
            </Typography>

            <TextField
              fullWidth
              label="System Prompt"
              value={formValues.prompt}
              onChange={(e) => handleChange('prompt', e.target.value)}
              error={!!formErrors.prompt}
              helperText={formErrors.prompt || 'The system-level instruction/prompt'}
              disabled={!isEditing || isReadOnly || updateMutation.isPending}
              required
              multiline
              rows={12}
              InputProps={{
                style: { fontFamily: 'monospace', fontSize: '0.875rem' },
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
